"use strict";

const SECOND = 1000;
const MINUTE = 60 * SECOND;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;
const DATE0 = new Date(0);

/**
 * Converts something to Date. Returns undefined if provided argument is invalid date or can't be converted.
 *
 * @param {Number|String|Date|moment|Object} ts
 * @returns {Date|undefined}
 */
function validate(ts) {
	if (!ts) {
		return new Date(0);
	}

	if (ts instanceof Date) {
		if (!isNaN(ts.valueOf())) {
			return ts;
		}
		// It is Date, but it's invalid, so we return undefined.
		return undefined;
	}

	let ms = ts.valueOf();
	if (!isNaN(ms)) {
		// ts is number or can be converted to number
		return new Date(ms);
	}

	// Try it as a string.
	let s = ts.toString();
	ms = Date.parse(s);
	if (!isNaN(ms)) {
		// It can be converted to date, but we use original string, rather then timestamp, so we can keep timezone.
		return new Date(s);
	}

	return undefined;
}

/**
 * Create new date, equal to given something.
 *
 * @param {Number|String|Date|moment} ts
 * @returns {Date}
 */
function clone(ts) {
	return new Date(validate(ts).valueOf());
}

/**
 * Floor timestamp to nearest multiple of interval (such as DAY).
 *
 * @param {Number|String|Date|moment} ts - timestamp or date
 * @param interval - time interval in milliseconds
 * @returns {Date}
 */
function floor(ts, interval) {
	let t = validate(ts).valueOf();
	return new Date(t - (t % interval));
}

/**
 * Floors date to HOUR
 *
 * @param {Number|String|Date|moment} ts - timestamp or date
 * @returns {Date}
 */
function hour(ts) {
	return floor(ts, HOUR);
}

/**
 * Start of the day.
 *
 * @param {Number|String|Date|moment} ts - timestamp or date
 * @returns {Date}
 */
function day(ts) {
	let t = clone(ts);
	t.setHours(0, 0, 0, 0);
	return t;
}

/**
 * Add (or subtract) the number of days from date.
 *
 * Returned date will have the same time as ts,
 * even if change goes through day saving time change.
 *
 * @param {number|string|Date|moment} ts - timestamp or date
 * @param {number} days - number of days to add to current date. Can be negative.
 * @returns {Date}
 */
function addDays(ts, days) {
	let t = clone(ts);
	t.setDate(t.getDate() + days);
	return t;
}

/**
 * Add (or subtract) time interval from date. If interval is multiple of DAY.
 *
 * If `interval` is multiple of `DAY` returned date will have the same time as ts,
 * even if change goes through day saving time change.
 *
 * @param {number|string|Date|moment} ts - timestamp or date
 * @param {number} interval - number of milliseconds to add to current date. Can be negative.
 * @returns {Date}
 */
function alter(ts, interval) {
	if (interval % DAY === 0) {
		return addDays(ts, Math.floor(interval / DAY));
	} else {
		return new Date(validate(ts).valueOf() + interval);
	}
}


/**
 * Start of the year.
 *
 * @param {Number|String|Date|moment} ts - timestamp or date
 * @returns {Date}
 */
function year(ts) {
	let t = clone(ts);
	t.setMonth(0, 1);
	t.setHours(0, 0, 0, 0);
	return t;
}

/**
 * Get greater of two dates. If Date(s) instances given - method will try to return the same instance.
 * Otherwise the value(s) will be converted to Date.
 *
 * If one of the dates falsy or not given - method will return another date.
 * If both dates falsy - method will return DATE0 instance.
 *
 * @param {Number|String|Date|moment|Object} ts1
 * @param {Number|String|Date|moment|Object} [ts2]
 * @returns {Date}
 */
function max(ts1, ts2) {
	if (ts1 && ts2) {
		ts1 = validate(ts1);
		ts2 = validate(ts2);
		if (ts1.valueOf() >= ts2.valueOf()) {
			return ts1;
		} else {
			return ts2;
		}
	}

	let ts = ts1 || ts2;
	if (ts) {
		return validate(ts);
	} else {
		return DATE0;
	}
}

/**
 * Return lesser of two dates. If Date(s) instances given - method will try to return the same instance.
 * Otherwise value(s) will be converted to Date.
 *
 * If one of the dates falsy or not given - method will return other date.
 * If both dates falsy - method will return DATE0 instance.
 *
 * @param {Number|String|Date|moment|Object} ts1
 * @param {Number|String|Date|moment|Object} [ts2]
 * @returns {Date}
 */
function min(ts1, ts2) {
	if (ts1 && ts2) {
		ts1 = validate(ts1);
		ts2 = validate(ts2);
		if (ts1.valueOf() <= ts2.valueOf()) {
			return ts1;
		} else {
			return ts2;
		}
	}

	let ts = ts1 || ts2;
	if (ts) {
		return validate(ts);
	} else {
		return DATE0;
	}
}

function daysDomain(from, to) {
	let domain = [];
	let i = clone(from);
	i.setHours(0, 0, 0, 0);
	domain.push(i);
	while (i < to) {
		i = clone(i);
		i.setDate(i.getDate() + 1);
		i.setHours(0, 0, 0, 0);
		domain.push(i);
	}
	return domain;
}

function hoursDomain(step, from, to) {
	let domain = [];
	let i = clone(from);
	let hour = i.getHours();
	let throughMidnight = (24 % step) && (hour % step === 0);
	hour = hour - hour % step;
	i.setHours(hour, 0, 0, 0);
	domain.push(i);
	while (i < to) {
		i = clone(i);
		let date = i.getDate();
		i.setHours(i.getHours() + step, 0, 0, 0);
		if (i.getDate() > date && throughMidnight) {
			i.setHours(0, 0, 0, 0);
		}
		domain.push(i);
	}
	return domain;
}

function domain(from, to) {
	let range = Math.ceil((to - from) / DAY);
	if (range > 10) {
		return daysDomain(from, to);
	}
	if (range > 4) {
		return hoursDomain(3, from, to);
	}
	return hoursDomain(1, from, to);
}

function reduceToDomain(domain, date) {
	let l = domain.length;
	if (date < domain[0] || date > domain[l - 1]) {
		return undefined;
	}
	for (let i = l - 1; i--; i > 0) {
		let d = domain[i];
		if (date >= d) {
			return d;
		}
	}
}

module.exports = {
	validate: validate, clone: clone, floor: floor, day: day, year: year, hour: hour,
	min: min, max: max, addDays: addDays, alter: alter,
	daysDomain: daysDomain, hoursDomain: hoursDomain, domain: domain, reduceToDomain: reduceToDomain,
	SECOND: SECOND, MINUTE: MINUTE, HOUR: HOUR, DAY: DAY, DATE0: DATE0
};